import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Save, Plus, Trash2, X, CheckCircle, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function RoleManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', label: '', permissions: [] });

  // Reset form to initial state
  const resetRoleForm = () => {
    setNewRole({ name: '', label: '', permissions: [] });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        axios.get(`${API}/roles`),
        axios.get(`${API}/roles/available-permissions`)
      ]);
      setRoles(rolesRes.data);
      setAvailablePermissions(permissionsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (roleId, permissionId) => {
    setRoles(prevRoles => prevRoles.map(role => {
      if (role.id === roleId) {
        const hasPermission = role.permissions.includes(permissionId);
        
        return {
          ...role,
          permissions: hasPermission
            ? role.permissions.filter(p => p !== permissionId)
            : [...role.permissions, permissionId]
        };
      }
      return role;
    }));
  };

  const hasPermission = (role, permissionId) => {
    return role.permissions.includes(permissionId);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Admin rolü hariç tüm rolleri güncelle (admin değiştirilemez)
      const rolesToUpdate = roles.filter(role => role.name !== 'admin');
      
      await Promise.all(
        rolesToUpdate.map(role =>
          axios.patch(`${API}/roles/${role.id}`, {
            permissions: role.permissions
          })
        )
      );
      toast.success('Değişiklikler kaydedildi');
      fetchData();
    } catch (error) {
      toast.error('Değişiklikler kaydedilemedi');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    if (!newRole.name || !newRole.label) {
      toast.error('Rol adı ve etiketi gerekli');
      return;
    }

    try {
      await axios.post(`${API}/roles`, newRole);
      toast.success('Rol oluşturuldu');
      setShowCreateDialog(false);
      resetRoleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rol oluşturulamadı');
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Bu rolü silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success('Rol silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rol silinemedi');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">Erişim Reddedildi</h2>
          <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <button
            onClick={() => navigate('/users')}
            className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 transition-colors mb-2"
            data-testid="back-to-users-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Kullanıcılara Dön
          </button>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              Rol Yönetimi
              {/* Rol sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {roles.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">
            Rollere yetki atayın ve yönetin
            {roles.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {roles.length.toLocaleString('tr-TR')} rol</span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (open) resetRoleForm();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-cyan-200 text-cyan-700 hover:bg-cyan-50 w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Rol
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:w-[90vw] lg:w-full max-w-2xl max-h-[90vh] overflow-y-auto !p-0 !gap-0">
              {/* Close Button */}
              <button
                onClick={() => setShowCreateDialog(false)}
                className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
              >
                <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
              </button>
              
              <DialogHeader className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 px-4 sm:px-6 py-4 sm:py-5 rounded-t-lg">
                <DialogTitle className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  Yeni Rol Oluştur
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 sm:p-5 rounded-xl border-2 border-blue-200">
                  <Label className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    Rol Adı (İngilizce, küçük harf) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value.toLowerCase() })}
                    placeholder="örnek: custom_role"
                    className="bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-600 mt-2">Sistem içinde kullanılacak teknik isim (boşluksuz, küçük harf)</p>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-5 rounded-xl border-2 border-purple-200">
                  <Label className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                    Rol Etiketi (Türkçe) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={newRole.label}
                    onChange={(e) => setNewRole({ ...newRole, label: e.target.value })}
                    placeholder="örnek: Özel Rol"
                    className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-600 mt-2">Kullanıcıların göreceği Türkçe isim</p>
                </div>
                
                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t-2 border-gray-200 flex flex-col sm:flex-row gap-2 sm:gap-3 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 mt-4 sm:mt-6">
                  <Button 
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => setShowCreateDialog(false)}
                    className="flex-1 border-2 hover:bg-gray-100 min-h-[44px] py-3"
                  >
                    <X className="w-4 h-4 mr-2" />
                    İptal
                  </Button>
                  <Button 
                    onClick={createRole}
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold shadow-lg min-h-[44px] py-3"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Rol Oluştur
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button 
            onClick={saveChanges} 
            disabled={saving}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 w-full sm:w-auto"
          >
            <Save className="w-4 h-4 mr-2" />
            <span className="truncate">{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</span>
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
        <p className="text-xs sm:text-sm text-gray-700">
          <strong>💡 İpucu:</strong> Checkbox'ları işaretleyerek rollere yetki atayabilirsiniz. 
          <strong className="text-cyan-700"> Admin rolü her zaman tüm yetkilere sahiptir ve düzenlenemez.</strong> Değişikliklerinizi kaydetmeyi unutmayın.
        </p>
      </div>

      {/* Permissions Table */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-cyan-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b-2 border-cyan-200">
              <tr>
                <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-900 sticky left-0 bg-gradient-to-r from-cyan-50 to-blue-50 z-10">
                  Yetki
                </th>
                {roles.map(role => (
                  <th key={role.id} className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center min-w-[100px] sm:min-w-[120px]">
                    <div className="flex flex-col items-center gap-1 sm:gap-2">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900">{role.label}</span>
                      <span className="text-xs text-gray-500">({role.name})</span>
                      {!role.isSystem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRole(role.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {availablePermissions.map(permission => (
                <tr key={permission.id} className="hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 transition-colors">
                  <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 sticky left-0 bg-white z-10">
                    <div>
                      <div className="text-sm sm:text-base font-medium text-gray-900">{permission.label}</div>
                      <div className="text-xs sm:text-sm text-gray-500">{permission.description}</div>
                    </div>
                  </td>
                  {roles.map(role => {
                    // Admin rolü her zaman tüm yetkilere sahip olmalı ve düzenlenemez
                    const isAdmin = role.name === 'admin';
                    const isDisabled = isAdmin; // Admin rolü için tüm checkboxlar disabled
                    
                    return (
                      <td key={`${role.id}-${permission.id}`} className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAdmin ? true : hasPermission(role, permission.id)}
                            onCheckedChange={() => !isAdmin && togglePermission(role.id, permission.id)}
                            disabled={isDisabled}
                            className={isAdmin ? 'opacity-50' : ''}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
